import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { FiDownload, FiPrinter, FiPlus, FiCopy, FiTrash2, FiLayers } from 'react-icons/fi';
import MainLayout from '../components/MainLayout';
import { useSubscription } from '../hooks/useSubscription';
import ErrorDialog from '../components/ErrorDialog';

export default function QRCodesPage() {
    const navigate = useNavigate();
    const [restaurantId, setRestaurantId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tableCount, setTableCount] = useState(1);
    const [deletedTables, setDeletedTables] = useState([]);
    const [restaurantName, setRestaurantName] = useState('GetYourQR');
    const [errorDialog, setErrorDialog] = useState({ isOpen: false, title: '', message: '' });
    const [copiedTable, setCopiedTable] = useState(null);

    // Confirm states
    const [confirmAdd, setConfirmAdd] = useState(false);          // single add confirm
    const [confirmDelete, setConfirmDelete] = useState(null);     // delete confirm (table number)
    const [showBulkDialog, setShowBulkDialog] = useState(false);  // bulk add dialog
    const [bulkCount, setBulkCount] = useState(1);
    const [confirmBulk, setConfirmBulk] = useState(false);        // bulk confirmation step

    const { limits, canAddQR } = useSubscription(restaurantId);

    // ─── Load from DB ───────────────────────────────────────────────────────
    useEffect(() => {
        async function loadData() {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return navigate('/login');
            const { data: rest } = await supabase
                .from('restaurants')
                .select('id, plan, name, table_count, deleted_qr_tables')
                .eq('owner_id', user.id)
                .single();
            if (!rest) return;
            setRestaurantId(rest.id);
            if (rest.name) setRestaurantName(rest.name);

            // Use DB value; if missing, default to 1 (never default to plan-based count)
            const dbCount = rest.table_count;
            setTableCount(typeof dbCount === 'number' ? dbCount : 1);

            // Deleted tables from DB column
            setDeletedTables(Array.isArray(rest.deleted_qr_tables) ? rest.deleted_qr_tables : []);
            setLoading(false);
        }
        loadData();
    }, [navigate]);

    // ─── Visible tables ──────────────────────────────────────────────────────
    const visibleTables = Array.from({ length: tableCount + deletedTables.length }, (_, i) => i + 1)
        .filter(n => !deletedTables.includes(n))
        .slice(0, tableCount);

    // ─── Persist to DB ───────────────────────────────────────────────────────
    const persistToDB = useCallback(async (newCount, newDeleted) => {
        if (!restaurantId) return;
        await supabase
            .from('restaurants')
            .update({ table_count: newCount, deleted_qr_tables: newDeleted })
            .eq('id', restaurantId);
    }, [restaurantId]);

    // ─── Add single table (with confirm) ─────────────────────────────────────
    const handleAddTable = () => {
        if (!canAddQR(visibleTables.length)) {
            setErrorDialog({
                isOpen: true,
                title: 'Limit Reached',
                message: `Your current plan allows up to ${limits.maxQrs} QR codes. Please upgrade to add more.`
            });
            return;
        }
        setConfirmAdd(true);
    };

    const confirmAddTable = () => {
        const newCount = tableCount + 1;
        const newDeleted = deletedTables.filter(t => t !== newCount);
        setTableCount(newCount);
        setDeletedTables(newDeleted);
        persistToDB(newCount, newDeleted);
        setConfirmAdd(false);
    };

    // ─── Bulk Add ────────────────────────────────────────────────────────────
    const handleStartBulk = () => {
        setBulkCount(1);
        setConfirmBulk(false);
        setShowBulkDialog(true);
    };

    const handleBulkProceed = () => {
        const n = parseInt(bulkCount, 10);
        if (!n || n < 1) return;
        if (!canAddQR(visibleTables.length + n)) {
            setErrorDialog({
                isOpen: true,
                title: 'Limit Reached',
                message: `Your current plan allows up to ${limits.maxQrs} QR codes. Adding ${n} would exceed the limit.`
            });
            return;
        }
        setConfirmBulk(true);
    };

    const confirmBulkAdd = () => {
        const n = parseInt(bulkCount, 10);
        const newCount = tableCount + n;
        const newDeleted = deletedTables.filter(t => t > tableCount + n);
        setTableCount(newCount);
        setDeletedTables(newDeleted);
        persistToDB(newCount, newDeleted);
        setShowBulkDialog(false);
        setConfirmBulk(false);
    };

    // ─── Delete ───────────────────────────────────────────────────────────────
    const confirmDeleteQR = () => {
        const updated = [...deletedTables, confirmDelete].sort((a, b) => a - b);
        setDeletedTables(updated);
        // Table count stays same (capacity doesn't decrease, table is just deleted)
        persistToDB(tableCount, updated);
        setConfirmDelete(null);
    };

    // ─── Copy URL ─────────────────────────────────────────────────────────────
    const getMenuUrl = (tableNumber) => `${window.location.origin}/menu/${restaurantId}?table=${tableNumber}`;

    const handleCopyLink = (tableNumber) => {
        navigator.clipboard.writeText(getMenuUrl(tableNumber)).then(() => {
            setCopiedTable(tableNumber);
            setTimeout(() => setCopiedTable(null), 2000);
        });
    };

    // ─── Download QR ─────────────────────────────────────────────────────────
    const downloadQR = (tableNumber) => {
        const svgEl = document.getElementById(`qr-svg-${tableNumber}`)?.querySelector('svg');
        if (!svgEl) return;
        const svgData = new XMLSerializer().serializeToString(svgEl);
        const blob = new Blob([svgData], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `table-${tableNumber}-qr.svg`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const nextTableNumber = tableCount + 1;
    const bulkFrom = tableCount + 1;
    const bulkTo = tableCount + parseInt(bulkCount || 0, 10);

    return (
        <MainLayout
            activeLink="QR Codes"
            title={
                <div>
                    <span className="block">Table QR Codes</span>
                    <span className="block text-xs text-slate-400 font-medium font-normal mt-0.5">
                        Print and place on tables · {visibleTables.length} QR code{visibleTables.length !== 1 ? 's' : ''}
                    </span>
                </div>
            }
            topNavChildren={
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleStartBulk}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white font-bold text-slate-600 hover:bg-slate-50 transition-colors shadow-sm text-sm"
                    >
                        <FiLayers className="w-4 h-4" /> Bulk Add
                    </button>
                    <button
                        onClick={handleAddTable}
                        className="flex items-center gap-2 bg-primary text-charcoal font-black text-sm px-4 py-2.5 rounded-xl hover:bg-primary/80 transition-colors shadow-sm"
                    >
                        <FiPlus className="w-4 h-4" /> Add Table
                    </button>
                    <button
                        onClick={() => window.print()}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white font-bold text-slate-600 hover:bg-slate-50 transition-colors shadow-sm text-sm"
                    >
                        <FiPrinter className="w-4 h-4" /> Print All
                    </button>
                </div>
            }
        >
            {/* ─── Single Add Confirm Modal ──────────────────────────── */}
            {confirmAdd && (
                <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 shadow-2xl w-full max-w-sm">
                        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                            <FiPlus className="w-6 h-6 text-charcoal" />
                        </div>
                        <h3 className="text-lg font-black text-charcoal mb-1">Add Table {nextTableNumber}?</h3>
                        <p className="text-sm text-slate-500 mb-6">
                            This will create a new QR code for <strong>Table {nextTableNumber}</strong>.
                            Once added, this action <span className="text-red-600 font-bold">cannot be undone</span>.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setConfirmAdd(false)}
                                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmAddTable}
                                className="flex-1 py-2.5 rounded-xl bg-primary text-charcoal font-black text-sm hover:bg-primary/80"
                            >
                                Yes, Add Table
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Bulk Add Dialog ────────────────────────────────────── */}
            {showBulkDialog && (
                <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 shadow-2xl w-full max-w-sm">
                        <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                            <FiLayers className="w-6 h-6 text-blue-600" />
                        </div>
                        <h3 className="text-lg font-black text-charcoal mb-1">Bulk Create QR Codes</h3>
                        <p className="text-sm text-slate-500 mb-4">How many new tables do you want to add?</p>

                        {!confirmBulk ? (
                            <>
                                <input
                                    type="number"
                                    min="1"
                                    max="50"
                                    value={bulkCount}
                                    onChange={e => setBulkCount(e.target.value)}
                                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-center text-2xl font-black text-charcoal mb-2 focus:outline-none focus:border-primary"
                                />
                                {parseInt(bulkCount, 10) > 0 && (
                                    <p className="text-xs text-slate-400 text-center mb-4">
                                        Will create Table {bulkFrom} to Table {bulkTo}
                                    </p>
                                )}
                                <div className="flex gap-3 mt-2">
                                    <button
                                        onClick={() => setShowBulkDialog(false)}
                                        className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleBulkProceed}
                                        disabled={!parseInt(bulkCount, 10) || parseInt(bulkCount, 10) < 1}
                                        className="flex-1 py-2.5 rounded-xl bg-charcoal text-white font-black text-sm hover:bg-slate-800 disabled:opacity-40"
                                    >
                                        Continue
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                                    <p className="text-sm font-bold text-amber-800">⚠️ Please confirm</p>
                                    <p className="text-xs text-amber-700 mt-1">
                                        You are about to add <strong>{bulkCount} QR codes</strong> (Table {bulkFrom}—Table {bulkTo}).
                                        This <span className="font-bold text-red-600">cannot be undone</span>.
                                    </p>
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setConfirmBulk(false)}
                                        className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50"
                                    >
                                        Go Back
                                    </button>
                                    <button
                                        onClick={confirmBulkAdd}
                                        className="flex-1 py-2.5 rounded-xl bg-primary text-charcoal font-black text-sm hover:bg-primary/80"
                                    >
                                        Confirm Add {bulkCount}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* ─── Delete Confirm Modal ────────────────────────────────── */}
            {confirmDelete && (
                <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 shadow-2xl w-full max-w-sm border-2 border-red-100">
                        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                            <FiTrash2 className="w-6 h-6 text-red-600" />
                        </div>
                        <h3 className="text-lg font-black text-charcoal mb-1">Delete Table {confirmDelete} QR?</h3>
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5">
                            <p className="text-sm font-black text-red-700">⚠️ DANGER — Permanent Action</p>
                            <p className="text-xs text-red-600 mt-1 leading-relaxed">
                                Once deleted, <strong>Table {confirmDelete}'s QR code will be permanently gone</strong> and the same QR link will
                                <span className="font-black"> never be recreated</span>. Any customer who scans the old QR will get an error.
                            </p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setConfirmDelete(null)}
                                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50"
                            >
                                Keep QR
                            </button>
                            <button
                                onClick={confirmDeleteQR}
                                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-black text-sm hover:bg-red-600"
                            >
                                Yes, Delete Permanently
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <main className="p-6 lg:p-8">
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="bg-white p-6 rounded-2xl border border-[#F4F2E6] animate-pulse flex flex-col items-center">
                                <div className="w-full flex justify-between items-center mb-4">
                                    <div className="h-3 w-12 bg-slate-200 rounded" />
                                    <div className="h-6 w-16 bg-slate-200 rounded-lg" />
                                </div>
                                <div className="w-[150px] h-[150px] bg-slate-100 rounded-xl mb-4" />
                                <div className="h-2 w-full bg-slate-100 rounded mb-4" />
                                <div className="h-10 w-full bg-slate-100 rounded-xl" />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 print:hidden">
                        {visibleTables.map(table => (
                            <div
                                key={table}
                                className="bg-white p-6 rounded-2xl border border-[#F4F2E6] shadow-sm flex flex-col items-center group hover:border-primary/30 hover:shadow-md transition-colors relative"
                            >
                                {/* Delete button */}
                                <button
                                    onClick={() => setConfirmDelete(table)}
                                    className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                                    title="Delete this QR code"
                                >
                                    <FiTrash2 className="w-4 h-4" />
                                </button>

                                <div className="w-full flex justify-between items-center mb-4">
                                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Dine-in</span>
                                    <span className="text-base font-black text-charcoal bg-[#FCFAF5] px-3 py-1 rounded-lg">Table {table}</span>
                                </div>

                                <div id={`qr-svg-${table}`} className="p-3 bg-white border-2 border-slate-100 rounded-xl mb-4 shadow-sm group-hover:border-primary/20 transition-colors">
                                    <QRCodeSVG
                                        value={getMenuUrl(table)}
                                        size={150}
                                        level="Q"
                                        includeMargin={true}
                                        fgColor="#1c1a0d"
                                    />
                                </div>

                                {/* URL display */}
                                <p className="text-[10px] text-slate-400 text-center mb-3 px-2 truncate w-full">
                                    {getMenuUrl(table)}
                                </p>

                                {/* Action buttons */}
                                <div className="flex gap-2 w-full">
                                    <button
                                        onClick={() => handleCopyLink(table)}
                                        className={`flex-1 py-2 rounded-xl font-bold flex items-center justify-center gap-1.5 text-xs transition-colors ${
                                            copiedTable === table
                                                ? 'bg-green-500 text-white'
                                                : 'bg-[#FCFAF5] text-slate-600 hover:bg-primary hover:text-charcoal'
                                        }`}
                                    >
                                        <FiCopy className="w-3.5 h-3.5" />
                                        {copiedTable === table ? 'Copied!' : 'Copy Link'}
                                    </button>
                                    <button
                                        onClick={() => downloadQR(table)}
                                        className="flex-1 py-2 rounded-xl bg-[#FCFAF5] text-slate-600 font-bold hover:bg-primary hover:text-charcoal transition-colors flex items-center justify-center gap-1.5 text-xs"
                                    >
                                        <FiDownload className="w-3.5 h-3.5" /> Download
                                    </button>
                                </div>
                            </div>
                        ))}

                        {/* Empty state */}
                        {visibleTables.length === 0 && (
                            <div className="col-span-full flex flex-col items-center justify-center py-20 text-center">
                                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
                                    <FiPlus className="w-8 h-8 text-primary" />
                                </div>
                                <h3 className="text-lg font-black text-charcoal mb-1">No QR codes yet</h3>
                                <p className="text-slate-400 text-sm mb-5">Click "Add Table" to create your first QR code.</p>
                                <button onClick={handleAddTable} className="bg-primary text-charcoal font-black px-6 py-3 rounded-xl hover:bg-primary/80 transition-colors">
                                    + Add First Table
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </main>

            {/* Print Layout */}
            <div className="hidden print:grid grid-cols-2 gap-8 p-8 bg-white">
                {visibleTables.map(table => (
                    <div key={`print-${table}`} className="border-4 border-charcoal p-8 flex flex-col items-center justify-center rounded-3xl break-inside-avoid mb-8 h-[500px]">
                        <h2 className="text-4xl font-black text-charcoal mb-2 uppercase tracking-wide">{restaurantName}</h2>
                        <p className="text-lg font-bold text-slate-500 mb-12">Scan to Order</p>
                        <QRCodeSVG value={getMenuUrl(table)} size={240} level="H" includeMargin={true} fgColor="#1c1a0d" />
                        <div className="mt-12 bg-charcoal text-white text-3xl font-black px-12 py-4 rounded-full">TABLE {table}</div>
                    </div>
                ))}
            </div>

            <ErrorDialog
                isOpen={errorDialog.isOpen}
                title={errorDialog.title}
                message={errorDialog.message}
                onClose={() => setErrorDialog({ ...errorDialog, isOpen: false })}
            />
        </MainLayout>
    );
}
